import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import './AutoJobApply.css';
import ProductSidebar from './ProductSidebar';
import { useToast } from './Toast/ToastProvider';
import LiveTab from './AutoJobApply/LiveTab';
import QueueTab from './AutoJobApply/QueueTab';

// Login-less ATS platforms run via the server-side Puppeteer agent.
// Everything else runs via the Chrome extension (when installed) so it uses
// the user's real, logged-in browser session.
const SERVER_PLATFORMS = new Set(['greenhouse', 'lever']);

const AutoJobApply = ({ embedded = false } = {}) => {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const toast = useToast();

  // ── Plan gating ──
  // Paid users get the full Auto Apply experience (24/7 lane + Quick).
  // Free users get Quick Apply only, with 5 lifetime sessions
  // (credits_auto_apply) and each session hard-capped to 5 applies.
  const _subEnd  = user?.subscription_end ? new Date(user.subscription_end) : null;
  const _inGrace = !_subEnd || new Date() < _subEnd;
  const hasActiveSubscription =
    !!user
    && (user.subscription_status === 'active' || user.subscription_status === 'cancelling')
    && _inGrace;
  const FREE_QUICK_APPLY_MAX = 5;     // hard cap on applies per free session
  const [quickApplyCredits, setQuickApplyCredits] = useState(null); // null = unknown

  // Redirect to home if the session is invalid (e.g. logged out on another
  // device — the verify-token call clears localStorage and sets user=null).
  useEffect(() => {
    if (embedded) return; // parent landing handles un-auth UX
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate, embedded]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ applied: 0, skipped: 0, failed: 0 });
  const [wsConnected, setWsConnected] = useState(false);
  const [showLinkedInWarning, setShowLinkedInWarning] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [resumePath, setResumePath] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [canStart, setCanStart] = useState(false);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const consoleEndRef = useRef(null);
  const consoleBodyRef = useRef(null);
  const extSessionIdRef = useRef(null);
  const savedJobKeysRef = useRef(new Set()); // tracks jobs already persisted to DB

  // ── Approval queue (AIApply-style) — minimal UI for testing ──
  const [queueJobs, setQueueJobs] = useState([]);
  const [queueCounts, setQueueCounts] = useState({ waiting: 0, approved: 0, applied: 0, failed: 0, rejected: 0, total: 0 });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueRefreshing, setQueueRefreshing] = useState(false);
  const [queueStatusFilter, setQueueStatusFilter] = useState('waiting');
  const [queueMode, setQueueMode] = useState('hybrid');
  const [queueThreshold, setQueueThreshold] = useState(70);
  // Nylas connection state — drives the "Connect Gmail" UI for OTP
  // auto-resolution + in-app inbox. configured=false means the server
  // doesn't have NYLAS_API_KEY etc. set, so we hide the button entirely.
  const [nylas, setNylas] = useState({ connected: false, email: null, configured: false, loading: true });
  // Usage / plan state — shown above the queue table.
  const [usage, setUsage] = useState(null); // { plan, applies24_7:{used,limit}, resumeTailoring:{used,limit}, resetsInSeconds, queuePaused, approvedAwaiting }
  const [resetCountdown, setResetCountdown] = useState(0);

  // Active tab on the run/results step. Splits the experience into the two
  // clearly-different products: live browser-extension automation (LinkedIn,
  // Indeed, Dice, Monster) vs the server-side 24/7 background queue
  // (Greenhouse, Lever, Workable, Ashby).
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'queue'
  // Per-lane active flags. Set true in handleLaunch when that lane is launched;
  // set false in the lane's completion handler. UI flips to idle only when both
  // are false. Per-lane gating prevents stale EXT_UPDATE / ws events (from a
  // previous session or a passively-listening extension) from finishing a lane
  // we never started.
  const extLaneActiveRef = useRef(false);
  const serverLaneActiveRef = useRef(false);
  const extStatsRef = useRef({ applied: 0, skipped: 0, failed: 0 });
  const serverStatsRef = useRef({ applied: 0, skipped: 0, failed: 0 });

  // Each lane writes its own running totals to its ref; displayed stats are the sum.
  const recomputeMergedStats = useCallback(() => {
    setStats({
      applied: (extStatsRef.current.applied || 0) + (serverStatsRef.current.applied || 0),
      skipped: (extStatsRef.current.skipped || 0) + (serverStatsRef.current.skipped || 0),
      failed:  (extStatsRef.current.failed  || 0) + (serverStatsRef.current.failed  || 0),
    });
  }, []);

  const finishExtLane = useCallback(() => {
    if (!extLaneActiveRef.current) return;
    extLaneActiveRef.current = false;
    if (!serverLaneActiveRef.current) setIsRunning(false);
  }, []);

  const finishServerLane = useCallback(() => {
    if (!serverLaneActiveRef.current) return;
    serverLaneActiveRef.current = false;
    if (!extLaneActiveRef.current) setIsRunning(false);
  }, []);

  const getApiBase = () => {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env) return env;
    const origin = window.location.origin;
    if (origin.startsWith('http://') || origin.startsWith('https://')) return origin;
    return 'http://localhost:5000'; // fallback for Electron
  };
  const API_BASE_URL = getApiBase();
  const WS_URL = API_BASE_URL.replace(/^http/, 'ws');
  
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [profileResume, setProfileResume] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
const [extInstalled, setExtInstalled] = useState(false);
  

  // Profile mirrors the full schema on /profile so the AI screening
  // pass has every field it might be asked about — work authorization,
  // sponsorship needs, willingness to relocate, employment type,
  // certifications, and the resume-derived work/education history.
  // Without these, screening questions like "Are you authorized to
  // work in the US?" or "Will you require visa sponsorship?" couldn't
  // be answered by the LLM and the form stalled.
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
    github: '',
    portfolio: '',
    currentTitle: '',
    jobTitle: '',
    yearsExperience: '',
    skills: '',
    professionalSummary: '',
    certifications: '',
    workAuthorization: '',
    salaryExpectation: '',
    expectedSalary: '',
    willingToRelocate: false,
    desiredEmploymentType: '',
    noticePeriod: 'Immediate',
    workExperience: [],
    education: [],
    projects: [],
  });

  const [preferences, setPreferences] = useState({
    jobTitles: '',
    locations: '',
    jobType: 'Full-time',
    remote: 'Any',
    minSalary: '',
    excludeCompanies: '',
    keywords: '',
    platforms: { linkedin: true, indeed: false, dice: false, monster: false },
    maxApplications: '20',
    // OFF by default — free users can't use AI tailoring (returns 403 on
    // every call), and even paid users should opt in explicitly so they
    // know they're spending tokens.
    autoTailor: false,
  });

  // Fetch the user's Quick Apply credit balance once we know who they
  // are. Paid users get the unlimited sentinel and we skip rendering the
  // counter at all. Re-fetched after each deduct in handleLaunch.
  const fetchQuickApplyCredits = React.useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/user/credits`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      const n = Number(data?.featureCredits?.autoApply);
      setQuickApplyCredits(Number.isFinite(n) ? n : null);
    } catch { /* network errors are non-fatal — UI just hides the counter */ }
  }, [user]);
  useEffect(() => { fetchQuickApplyCredits(); }, [fetchQuickApplyCredits]);

  // Detect Chrome extension
  useEffect(() => {
    const onReady = () => { setExtInstalled(true); };
    window.addEventListener('terview-ext-ready', onReady);
    // Ping in case bridge already loaded
    window.postMessage({ terviewExt: true, type: 'EXT_PING' }, '*');
    const onPong = (e) => { if (e.data?.terviewPage && e.data.type === 'EXT_PONG') onReady(); };
    window.addEventListener('message', onPong);
    // Also check DOM attribute
    if (document.documentElement.getAttribute('data-terview-ext')) onReady();
    return () => {
      window.removeEventListener('terview-ext-ready', onReady);
      window.removeEventListener('message', onPong);
    };
  }, []);

  // Listen for live updates from extension
  useEffect(() => {
    if (!extInstalled) return;
    const handler = async (e) => {
      if (!e.data?.terviewPage || e.data.type !== 'EXT_UPDATE') return;
      // Only process extension updates if we launched the ext lane this session
      // OR the extension is actively running (sync-up case after a page reload).
      // Without this gate, a passively-listening extension can flush its stale
      // logs/stats into the UI and clobber the server lane's output.
      if (!extLaneActiveRef.current && !e.data.running) return;

      // Sync UI to extension state — if extension is running but UI thinks it isn't,
      // flip the button to "Stop Agent" so the user can control it.
      if (e.data.running && !isRunning) {
        extLaneActiveRef.current = true;
        setIsRunning(true);
      }

      extStatsRef.current = {
        applied: e.data.stats?.applied || 0,
        skipped: e.data.stats?.skipped || 0,
        failed:  e.data.stats?.failed  || 0,
      };
      recomputeMergedStats();
      if (e.data.logs?.length) {
        setLogs(e.data.logs.map(l => ({ message: l.message, type: l.type, time: l.time })));
      }
      if (e.data.appliedJobs !== undefined) {
        const rawJobs = (e.data.appliedJobs || []).filter(j => j.status !== 'skipped');
        // Dedup: the extension can push the same job twice — once as
        // 'applying' when it queues, and again with a final status. Collapse
        // to a single entry per (title|company|platform) and keep the entry
        // with the highest-priority status (applied > failed > applying) so
        // a real "Applied" never gets overwritten by a stale "applying" or
        // an in-flight "failed" placeholder.
        const STATUS_RANK = { applied: 3, failed: 2, applying: 1 };
        const byKey = new Map();
        for (const j of rawJobs) {
          const key = `${j.title || ''}|${j.company || ''}|${j.platform || ''}`;
          const cur = byKey.get(key);
          if (!cur || (STATUS_RANK[j.status] || 0) >= (STATUS_RANK[cur.status] || 0)) {
            byKey.set(key, j);
          }
        }
        const jobs = Array.from(byKey.values());
        setAppliedJobs(jobs.map(j => ({
          title: j.title,
          company: j.company,
          location: j.location,
          platform: j.platform,
          status: j.status,
          appliedAt: j.appliedAt,
          logoUrl: j.logoUrl || null,
          postedDate: j.postedDate || '',
          salary: j.salary || '',
          employmentType: j.employmentType || '',
          description: j.description || '',
        })));

        // Persist new finalized jobs to DB
        if (extSessionIdRef.current) {
          const token = localStorage.getItem('token');
          for (const j of jobs) {
            if (j.status === 'applying') continue;
            const key = `${j.title}|${j.platform}|${j.appliedAt}`;
            if (savedJobKeysRef.current.has(key)) continue;
            savedJobKeysRef.current.add(key);
            fetch(`${API_BASE_URL}/api/auto-apply/ext-job`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ sessionId: extSessionIdRef.current, job: j })
            }).catch(() => {});
          }
        }
      }
      // Session finished — save to DB, then reset UI after a short delay
      if (!e.data.running && isRunning) {
        setAppliedJobs(prev => prev.map(j => j.status === 'applying' ? { ...j, status: 'failed' } : j));
        if (extSessionIdRef.current) {
          fetch(`${API_BASE_URL}/api/auto-apply/ext-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ sessionId: extSessionIdRef.current, stats: e.data.stats })
          }).catch(() => {});
          extSessionIdRef.current = null;
        }
        finishExtLane();

      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [extInstalled, isRunning]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRunning) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRunning]);

  const [showNavBlockModal, setShowNavBlockModal] = useState(false);
  const [pendingNavUrl, setPendingNavUrl] = useState(null);

  useEffect(() => {
    if (!isRunning) return;
    const handleClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto')) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavUrl(href);
      setShowNavBlockModal(true);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isRunning]);

  useEffect(() => {
    if (consoleBodyRef.current) {
      consoleBodyRef.current.scrollTop = consoleBodyRef.current.scrollHeight;
    }
  }, [logs]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.isRunning) { setIsRunning(true); connectWebSocket(); }
      } catch {}
    };
    if (user) checkStatus();
  }, [user]);

  useEffect(() => {
    setCanStart(false);
  }, [preferences.platforms]);

  // ── Nylas: load connection status + handle OAuth redirect ──────────
  // On mount and whenever the user changes, hit /api/nylas/status to drive
  // the Connect Gmail button. Also clean up the ?nylas=connected query
  // param the callback redirected us with.
  const fetchNylasStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/nylas/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setNylas({
        connected: !!data.connected,
        email: data.email || null,
        configured: !!data.configured,
        loading: false,
      });
    } catch {
      setNylas(prev => ({ ...prev, loading: false }));
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (!user) return;
    fetchNylasStatus();
    // Strip ?nylas=connected | ?nylas=error from URL after we've seen it.
    const params = new URLSearchParams(window.location.search);
    if (params.has('nylas')) {
      const result = params.get('nylas');
      if (result === 'connected') addClientLog('✅ Gmail connected — OTP and inbox ready.', 'success');
      else if (result === 'error')  addClientLog(`⚠️ Gmail connect failed: ${params.get('reason') || 'unknown'}`, 'warning');
      params.delete('nylas'); params.delete('reason');
      const search = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));
    }
  }, [user, fetchNylasStatus]);

  const connectNylas = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/nylas/auth`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        addClientLog(`Nylas auth start failed: ${e.error || res.status}`, 'warning');
        return;
      }
      const { authUrl } = await res.json();
      if (authUrl) window.location.href = authUrl; // full redirect to Nylas
    } catch (err) {
      addClientLog(`Nylas auth error: ${err.message}`, 'warning');
    }
  }, [API_BASE_URL]);

  const disconnectNylas = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/api/nylas/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNylas(prev => ({ ...prev, connected: false, email: null }));
      addClientLog('Gmail disconnected.', 'info');
    } catch (err) {
      addClientLog(`Nylas disconnect error: ${err.message}`, 'warning');
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchProfileResume = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {

          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.profile) {
          const filename = data.profile.resumeFilename || null;
          setProfileResume(filename);
          if (filename) {
            setResumeChoice('profile');
            setResumePath(`uploads/resumes/${filename}`);
            setResumeName(filename);
          }
          setProfile(prev => ({
            ...prev,
            fullName:           data.profile.fullName           || prev.fullName,
            email:              data.profile.email              || prev.email,
            phone:              data.profile.phone              || prev.phone,
            location:           data.profile.location           || prev.location,
            linkedIn:           data.profile.linkedIn           || prev.linkedIn,
            github:             data.profile.github             || prev.github,
            portfolio:          data.profile.portfolio          || prev.portfolio,
            currentTitle:       data.profile.jobTitle           || prev.currentTitle,
            jobTitle:           data.profile.jobTitle           || prev.jobTitle,
            yearsExperience:    data.profile.yearsExperience    || prev.yearsExperience,
            skills:             data.profile.skills             || prev.skills,
            professionalSummary: data.profile.professionalSummary || prev.professionalSummary,
            // Additional fields the LLM uses to answer screening Qs:
            // visa sponsorship, salary expectation, relocation, etc.
            certifications:     data.profile.certifications     || prev.certifications,
            workAuthorization:  data.profile.workAuthorization  || prev.workAuthorization,
            salaryExpectation:  data.profile.salaryExpectation  || prev.salaryExpectation,
            expectedSalary:     data.profile.salaryExpectation  || data.profile.expectedSalary || prev.expectedSalary,
            willingToRelocate:  data.profile.willingToRelocate !== undefined ? data.profile.willingToRelocate : prev.willingToRelocate,
            desiredEmploymentType: data.profile.desiredEmploymentType || prev.desiredEmploymentType,
            workExperience:     Array.isArray(data.profile.workExperience) ? data.profile.workExperience : prev.workExperience,
            education:          Array.isArray(data.profile.education)      ? data.profile.education      : prev.education,
            projects:           Array.isArray(data.profile.projects)       ? data.profile.projects       : prev.projects,
          }));
          // Hydrate queue settings from saved profile
          if (data.profile.autoApplyMode) setQueueMode(data.profile.autoApplyMode);
          if (typeof data.profile.matchThreshold === 'number') setQueueThreshold(data.profile.matchThreshold);
        }
      } catch {}
    };
    if (user) fetchProfileResume();
  }, [user]);

  const addClientLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
  };

  // ── Queue API helpers ──
  const fetchQueue = useCallback(async (statusOverride) => {
    const status = statusOverride ?? queueStatusFilter;
    setQueueLoading(true);
    try {
      const url = `${API_BASE_URL}/api/auto-apply/queue${status ? `?status=${status}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      if (res.ok) setQueueJobs(data.jobs || []);
    } catch (err) {
      console.error('fetchQueue:', err.message);
    } finally {
      setQueueLoading(false);
    }
  }, [API_BASE_URL, queueStatusFilter]);

  // Per-status counts that drive the badges on the filter chips. Cheap
  // GROUP BY on the backend so this is safe to call alongside fetchQueue.
  const fetchQueueCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/counts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (res.ok && data.counts) setQueueCounts(data.counts);
    } catch (err) {
      console.error('fetchQueueCounts:', err.message);
    }
  }, [API_BASE_URL]);

  const refreshQueue = async () => {
    setQueueRefreshing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (res.ok) {
        if (data.capped) {
          toast.warning(`Discovery cap reached (${data.capUsed}/${data.capLimit}). Resets at 00:00 UTC.`);
        } else if (data.stoppedAtCap) {
          toast.success(`Found ${data.discovered} new jobs · ${data.autoApproved} auto-approved (capped at daily limit)`);
        } else if (data.discovered === 0) {
          toast.info('No new jobs since last refresh.');
        } else {
          toast.success(`Found ${data.discovered} new jobs${data.autoApproved ? ` · ${data.autoApproved} auto-approved` : ''}`);
        }
      } else {
        toast.error(`Discovery failed: ${data.message}`);
      }
    } catch (err) {
      toast.error(`Discovery error: ${err.message}`);
    } finally {
      setQueueRefreshing(false);
      fetchQueue();
      fetchQueueCounts();
      fetchUsage();
    }
  };

  // Manually trigger the queue drain. Server returns immediately; the
  // background worker processes all currently-approved rows.
  const [queueDraining, setQueueDraining] = useState(false);
  const drainNow = async () => {
    setQueueDraining(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/drain`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(`Drain failed: ${data.message || 'unknown error'}`);
      } else if (data.alreadyDraining) {
        toast.info('A drain is already running — no new worker started.');
      } else if (data.pending === 0) {
        toast.info('No approved jobs to drain.');
      } else {
        toast.success(`Draining ${data.pending} job(s) in the background…`);
      }
    } catch (err) {
      toast.error(`Drain error: ${err.message}`);
    } finally {
      // Refresh counts after a few seconds so the badge starts updating
      // while the drain is mid-flight.
      setTimeout(() => { fetchQueue(); fetchQueueCounts(); fetchUsage(); }, 4000);
      setQueueDraining(false);
    }
  };

  // Fetch usage + plan + reset countdown. Called on mount, after each
  // refresh/approve/reject, and after pause-toggle.
  const fetchUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/usage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsage(data);
        setResetCountdown(data.resetsInSeconds || 0);
      }
    } catch (err) {
      console.error('fetchUsage:', err.message);
    }
  }, [API_BASE_URL]);

  // Tick the countdown every second so the "resets in 8h 23m" label stays live.
  useEffect(() => {
    if (!resetCountdown) return;
    const id = setInterval(() => setResetCountdown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resetCountdown]);

  // Load usage on mount + whenever the queue list changes.
  useEffect(() => {
    if (user) fetchUsage();
  }, [user, fetchUsage]);

  const togglePause = async () => {
    if (!usage) return;
    try {
      await fetch(`${API_BASE_URL}/api/auto-apply/queue/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ paused: !usage.queuePaused }),
      });
      fetchUsage();
    } catch (err) {
      addClientLog(`❌ Pause toggle error: ${err.message}`, 'error');
    }
  };

  // Third arg (job) is currently unused — kept in the signature because
  // QueueTab.jsx callers pass the full job row; if we re-introduce a
  // client-side branch we'll want it back without touching call sites.
  const decideJob = async (id, action, _job = null) => {
    try {
      // All approvals (Greenhouse included) go through the server-side
      // queue. The Chrome extension Greenhouse branch was removed
      // because it requires the user's browser to stay open — defeating
      // the point of an unattended auto-apply. Server-side Puppeteer is
      // the source of truth; Twilio/Airbnb/Datadog Remix-domain failures
      // are addressed inside jobApplyAgent.js, not by handing the work
      // back to the user's browser.
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/${id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(`${action === 'approve' ? 'Approve' : 'Reject'} failed: ${data.message}`);
      } else if (action === 'approve') {
        toast.success('Approved — applying in the background', { duration: 3500 });
      }
    } catch (err) {
      toast.error(`${action} error: ${err.message}`);
    } finally {
      fetchQueue();
      fetchQueueCounts();
      fetchUsage();
    }
  };

  const [queueSettingsSavedAt, setQueueSettingsSavedAt] = useState(null);
  const saveQueueSettings = async (mode, threshold) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ mode, threshold }),
      });
      if (res.ok) {
        setQueueSettingsSavedAt(Date.now());
        setTimeout(() => setQueueSettingsSavedAt(null), 2000);
      }
    } catch (err) {
      addClientLog(`❌ Settings save error: ${err.message}`, 'error');
    }
  };

  // Load queue on mount + whenever the status filter changes
  useEffect(() => {
    if (user) {
      fetchQueue();
      fetchQueueCounts();
    }
  }, [user, queueStatusFilter, fetchQueue, fetchQueueCounts]);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/ws/auto-apply?token=${token}`);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          setLogs(prev => [...prev, { message: data.message, type: data.level || 'info', time: data.time }]);
        } else if (data.type === 'job_result') {
          setAppliedJobs(prev => [...prev, data.job]);
          serverStatsRef.current[data.job.status] = (serverStatsRef.current[data.job.status] || 0) + 1;
          recomputeMergedStats();
        } else if (data.type === 'complete') {
          if (data.stats) {
            serverStatsRef.current = {
              applied: data.stats.applied || 0,
              skipped: data.stats.skipped || 0,
              failed:  data.stats.failed  || 0,
            };
            recomputeMergedStats();
          }
          setLogs(prev => [...prev, { message: '🎯 Server lane completed!', type: 'success', time: new Date().toLocaleTimeString() }]);
          finishServerLane();
        } else if (data.type === 'error') {
          setLogs(prev => [...prev, { message: `❌ ${data.message}`, type: 'error', time: new Date().toLocaleTimeString() }]);
          finishServerLane();
        }
      } catch {}
    };
    ws.onclose = () => { setWsConnected(false); wsRef.current = null; };
    ws.onerror = () => setWsConnected(false);
  }, [WS_URL]);

  useEffect(() => () => { if (wsRef.current) wsRef.current.close(); }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/upload-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) { setResumePath(data.resumePath); setResumeName(file.name); }
      else addClientLog(`❌ Upload failed: ${data.message}`, 'error');
    } catch (err) {
      addClientLog(`❌ Upload error: ${err.message}`, 'error');
    }
  };

  const handleLaunch = async () => {
    const errors = {};
    if (!preferences.jobTitles.trim()) errors.jobTitles = true;
    if (!preferences.locations.trim()) errors.locations = true;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.jobTitles) addClientLog('⚠️ Enter at least one job title', 'warning');
      if (errors.locations) addClientLog('⚠️ Enter at least one location', 'warning');
      return;
    }
    if (!Object.values(preferences.platforms).some(Boolean)) { addClientLog('⚠️ Select at least one platform', 'warning'); return; }

    // Split selected platforms into two lanes:
    //   serverPlatforms — login-less ATS (Greenhouse, Lever) → backend Puppeteer
    //   extPlatforms    — everything else → Chrome extension (real browser session)
    let serverPlatforms = {};
    const extPlatforms = {};
    for (const [k, v] of Object.entries(preferences.platforms)) {
      if (SERVER_PLATFORMS.has(k)) serverPlatforms[k] = v;
      else extPlatforms[k] = v;
    }
    let extEnabled = Object.values(extPlatforms).some(Boolean);
    let serverEnabled = Object.values(serverPlatforms).some(Boolean);

    // ── Free-tier gating ──
    if (!hasActiveSubscription) {
      // 24/7 lane is paid-only. Silently drop it from this launch and
      // tell the user — they keep going with the Quick Apply lane if
      // they picked any extension platforms.
      if (serverEnabled) {
        addClientLog('🔒 24/7 background applies are paid-only — skipping Greenhouse/Lever for this run.', 'warning');
        serverEnabled = false;
        serverPlatforms = {};
      }
      // If no extension platforms were selected either, there's nothing
      // free-tier can run — point them at the upgrade modal.
      if (!extEnabled) {
        addClientLog('⚠️ Pick LinkedIn, Dice, Indeed, or Monster to run Quick Apply on the free plan.', 'warning');
        window.dispatchEvent(new CustomEvent('openPricingModal'));
        return;
      }
      // Deduct a Quick Apply credit BEFORE we fire EXT_START so a free
      // user can't burn extension cycles past their 5-session budget.
      try {
        const dr = await fetch(`${API_BASE_URL}/api/auto-apply/deduct-credit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!dr.ok) {
          const d = await dr.json().catch(() => ({}));
          addClientLog(`🔒 ${d?.error || 'Out of Quick Apply credits — upgrade to keep applying.'}`, 'warning');
          window.dispatchEvent(new CustomEvent('openPricingModal'));
          return;
        }
        const d = await dr.json();
        if (typeof d?.remainingCredits === 'number') setQuickApplyCredits(d.remainingCredits);
      } catch (err) {
        addClientLog('⚠️ Could not verify Quick Apply credits — try again in a moment.', 'warning');
        return;
      }
      // Hard cap session size to 5 applies for free users, regardless of
      // what they typed into Max Applications.
      const requested = Number(preferences.maxApplications) || FREE_QUICK_APPLY_MAX;
      if (requested > FREE_QUICK_APPLY_MAX) {
        addClientLog(`ℹ️ Free Quick Apply is capped at ${FREE_QUICK_APPLY_MAX} jobs per session.`, 'info');
        preferences.maxApplications = FREE_QUICK_APPLY_MAX;
      }
    }

    setIsRunning(true);
    setLogs([]);
    setAppliedJobs([]);
    setStats({ applied: 0, skipped: 0, failed: 0 });
    extStatsRef.current = { applied: 0, skipped: 0, failed: 0 };
    serverStatsRef.current = { applied: 0, skipped: 0, failed: 0 };
    extLaneActiveRef.current = false;
    serverLaneActiveRef.current = false;

    // Local counter — used only to gate the "no lane launched" warning at the
    // bottom of this function. Distinct from the active-flag refs, which are
    // mutated by completion handlers and can flip back to false while we're
    // still mid-launch.
    let lanesLaunched = 0;

    // ── Lane 1: Chrome extension (LinkedIn / Indeed / Dice / Monster) ──
    if (extInstalled && extEnabled) {
      extLaneActiveRef.current = true;
      lanesLaunched++;
      addClientLog('⚡ Extension lane: launching for ' + Object.keys(extPlatforms).filter(k => extPlatforms[k]).join(', '), 'info');
      savedJobKeysRef.current = new Set();
      extSessionIdRef.current = null;

      // Force-disable AI Tailoring for any user whose plan can't actually
      // use it. Otherwise the extension fires /tailor-resume on every job,
      // gets a 403, logs a warning, and falls back — wasting time + cluttering
      // the console. The toggle is also gated in the UI for free users, but
      // this is the runtime safety net.
      const tailoringAllowed = (usage?.resumeTailoring?.limit ?? 0) > 0;
      const safePreferences = {
        ...preferences,
        platforms: extPlatforms,
        autoTailor: tailoringAllowed && preferences.autoTailor,
      };

      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/ext-start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ preferences: safePreferences, profile })
        });
        const data = await res.json();
        if (data.sessionId) extSessionIdRef.current = data.sessionId;
      } catch (err) {
        console.warn('[Terview] ext-start failed:', err.message);
      }
      window.postMessage({
        terviewExt: true,
        type: 'EXT_START',
        payload: {
          token: localStorage.getItem('token'),
          preferences: { ...safePreferences, profile, resumeFilename: profileResume },
        }
      }, '*');
    }

    // ── Lane 2: Server Puppeteer (Greenhouse / Lever) ──
    if (serverEnabled) {
      serverLaneActiveRef.current = true;
      lanesLaunched++;
      connectWebSocket();
      await new Promise(r => setTimeout(r, 600));
      addClientLog('🚀 Server lane: launching for ' + Object.keys(serverPlatforms).filter(k => serverPlatforms[k]).join(', '), 'info');
      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({
            profile,
            preferences: { ...preferences, platforms: serverPlatforms },
            resumePath: resumeChoice === 'profile' ? null : resumePath,
            useProfileResume: resumeChoice === 'profile'
          })
        });
        const data = await res.json();
        if (res.ok) {
          setSessionId(data.sessionId);
          addClientLog(`✅ Server session started (ID: ${data.sessionId})`, 'success');
        } else {
          addClientLog(`❌ ${data.message}`, 'error');
          finishServerLane();
        }
      } catch (err) {
        addClientLog(`❌ Server error: ${err.message}`, 'error');
        addClientLog('💡 Make sure your backend server is running with Puppeteer installed', 'warning');
        finishServerLane();
      }
    }

    // If we never kicked off a lane (e.g. extension not installed but only
    // ext-platforms were selected), bail out cleanly.
    if (lanesLaunched === 0) {
      addClientLog('⚠️ No lane was launched. Install the Chrome extension or select Greenhouse/Lever.', 'warning');
      setIsRunning(false);
    }
  };

  const handleStop = async () => {
    // Stop both lanes if they're running. Each is a no-op if its lane isn't active.
    if (extInstalled) {
      window.postMessage({ terviewExt: true, type: 'EXT_STOP' }, '*');
      if (extSessionIdRef.current) {
        try {
          await fetch(`${API_BASE_URL}/api/auto-apply/ext-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ sessionId: extSessionIdRef.current, stats })
          });
        } catch {}
        extSessionIdRef.current = null;
      }
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ action: 'stop' }));
    try { await fetch(`${API_BASE_URL}/api/auto-apply/stop`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }); } catch {}

    setAppliedJobs(prev => prev.map(j => j.status === 'applying' ? { ...j, status: 'failed' } : j));
    extLaneActiveRef.current = false;
    serverLaneActiveRef.current = false;
    setIsRunning(false);
    addClientLog('⏹️ Agent stopped', 'warning');
  };

  const [fieldErrors, setFieldErrors] = useState({});



  const renderStep1 = () => (
    <div className="ajp-form-grid">
      {/* Two top-level sections wrapped so the Electron pane can lay
          them out side-by-side (preferences left, platforms right)
          via a scoped CSS override. Web layout stays stacked. */}
      <div className="ajp-prefs-section">
        <h3 className="ajp-section-title">🎯 Job Preferences</h3>
        <div className="ajp-row">
          <div className="ajp-field ajp-field-full"><label>Job Titles <span className="ajp-required">*</span></label>
            <input className={fieldErrors.jobTitles ? 'ajp-field-error' : ''} value={preferences.jobTitles} onChange={e => { setPreferences({ ...preferences, jobTitles: e.target.value }); setFieldErrors(f => ({ ...f, jobTitles: false })); }} placeholder="Frontend Developer, React Engineer, UI Developer" />
          </div>
        </div>
        <div className="ajp-row">
          <div className="ajp-field"><label>Years of Experience</label><input value={profile.yearsExperience} onChange={e => setProfile({ ...profile, yearsExperience: e.target.value })} placeholder="e.g. 5" /></div>
          <div className="ajp-field"><label>Job Type</label>
            <select value={preferences.jobType} onChange={e => setPreferences({ ...preferences, jobType: e.target.value })}>
              <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
            </select>
          </div>
        </div>
        <div className="ajp-field"><label>Locations <span className="ajp-required">*</span></label>
          <input className={fieldErrors.locations ? 'ajp-field-error' : ''} value={preferences.locations} onChange={e => { setPreferences({ ...preferences, locations: e.target.value }); setFieldErrors(f => ({ ...f, locations: false })); }} placeholder="New York, Remote" />
        </div>
        <div className="ajp-row">
          <div className="ajp-field"><label>Remote Preference</label>
            <select value={preferences.remote} onChange={e => setPreferences({ ...preferences, remote: e.target.value })}>
              <option>Any</option><option>Remote Only</option><option>Hybrid</option><option>On-site Only</option>
            </select>
          </div>
          <div className="ajp-field"><label>Max Applications</label>
            <select value={preferences.maxApplications} onChange={e => setPreferences({ ...preferences, maxApplications: e.target.value })}>
              <option>10</option><option>20</option><option>30</option>
            </select>
          </div>
        </div>
      </div>

      <div className="ajp-platforms-section">
        <h3 className="ajp-section-title">🌐 Job Platforms</h3>
        <div className="ajp-platforms">
          {[{ key: 'linkedin', label: 'LinkedIn', domain: 'linkedin.com', icon: '🔷' },
            { key: 'indeed', label: 'Indeed', domain: 'indeed.com', icon: '🔵' },
            { key: 'dice', label: 'Dice', domain: 'dice.com', icon: '🎲' },
            { key: 'monster', label: 'Monster', domain: 'monster.com', icon: '👹' },
          ].map(({ key, label, domain, icon }) => (
            <label key={key} className={`ajp-platform-card ${preferences.platforms[key] ? 'enabled' : ''}`}>
              <input type="checkbox" checked={preferences.platforms[key]}
                onChange={() => setPreferences({ ...preferences, platforms: { ...preferences.platforms, [key]: !preferences.platforms[key] } })} />
              <span className="platform-icon">
                {/* Real brand logo. Tries Clearbit → Google favicon → emoji so
                    the genuine logo shows even if one source is blocked. */}
                <img
                  src={`https://logo.clearbit.com/${domain}`}
                  data-fb={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
                  alt={label}
                  width="24" height="24"
                  style={{ borderRadius: 6, display: 'block', objectFit: 'contain' }}
                  onError={e => {
                    const fb = e.currentTarget.getAttribute('data-fb');
                    if (fb) { e.currentTarget.removeAttribute('data-fb'); e.currentTarget.src = fb; }
                    else { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline'; }
                  }}
                />
                <span style={{ display: 'none' }}>{icon}</span>
              </span>
              <span className="platform-name">{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <h3 className="ajp-section-title">📄 Resume</h3>
      <div style={{ display: 'flex', gap: '6px' }}>
        {profileResume && (
          <button
            type="button"
            onClick={() => { setResumeChoice('profile'); setResumePath(`uploads/resumes/${profileResume}`); setResumeName(profileResume); }}
            style={{
              flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: '8px', cursor: 'pointer',
              border: `1.5px solid ${resumeChoice === 'profile' ? '#9370db' : 'rgba(255,255,255,0.15)'}`,
              background: resumeChoice === 'profile' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.04)',
              color: 'white', fontSize: '0.75rem', transition: 'all 0.2s', textAlign: 'left'
            }}
          >
            <div style={{ fontWeight: '600' }}>✅ Profile Resume</div>
            <div style={{ fontSize: '0.66rem', opacity: 0.55, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileResume}</div>
          </button>
        )}
        <button
          type="button"
          onClick={() => { setResumeChoice('upload'); fileInputRef.current?.click(); }}
          style={{
            flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: '8px', cursor: 'pointer',
            border: `1.5px solid ${resumeChoice === 'upload' ? '#9370db' : 'rgba(255,255,255,0.15)'}`,
            background: resumeChoice === 'upload' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.04)',
            color: 'white', fontSize: '0.75rem', transition: 'all 0.2s', textAlign: 'left'
          }}
        >
          <div style={{ fontWeight: '600' }}>📄 Upload Resume</div>
          <div style={{ fontSize: '0.66rem', opacity: 0.55, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumeName || 'Choose a file'}</div>
        </button>
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
          onChange={e => handleFileUpload(e.target.files[0])} />
      </div>

      {/* AI Tailoring lives on its own row so the resume picker stays a clean 2-up
          and the tailoring toggle has room to breathe (label + switch) without
          getting squeezed into a three-column row. */}
      {(() => {
        const tailoringBlocked = usage?.resumeTailoring?.limit === 0;
        const onClick = tailoringBlocked
          ? () => window.dispatchEvent(new CustomEvent('openPricingModal'))
          : () => setPreferences({ ...preferences, autoTailor: !preferences.autoTailor });
        const enabled = !tailoringBlocked && preferences.autoTailor;
        return (
          <button
            type="button"
            onClick={onClick}
            title={tailoringBlocked ? 'AI Tailoring is available on Pro and Max — click to upgrade' : ''}
            style={{
              width: '100%', padding: '12px 12px', borderRadius: '8px', cursor: 'pointer',
              border: `1.5px solid ${tailoringBlocked ? 'rgba(124,58,237,0.5)' : enabled ? 'rgba(74,222,128,0.5)' : 'rgba(248,113,113,0.4)'}`,
              background: tailoringBlocked ? 'rgba(124,58,237,0.10)' : enabled ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.08)',
              color: 'white', fontSize: '0.78rem', transition: 'all 0.2s', textAlign: 'left',
              opacity: tailoringBlocked ? 0.85 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <span style={{ fontWeight: '600', fontSize: '0.78rem', color: tailoringBlocked ? '#c4a3ff' : enabled ? '#4ade80' : '#f87171', whiteSpace: 'nowrap' }}>
                  {tailoringBlocked ? '🔒 AI Tailoring' : '🤖 AI Tailoring'}
                </span>
                <span style={{ fontSize: '0.66rem', opacity: 0.55, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tailoringBlocked ? 'Upgrade to Pro or Max' : 'Match keywords to job'}
                </span>
              </div>
              {!tailoringBlocked && (
                <div style={{
                  width: '30px', height: '16px', borderRadius: '8px', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                  background: enabled ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.15)',
                }}>
                  <div style={{
                    position: 'absolute', top: '2px', left: enabled ? '16px' : '2px',
                    width: '12px', height: '12px', borderRadius: '50%', transition: 'left 0.2s',
                    background: enabled ? '#4ade80' : 'rgba(255,255,255,0.5)',
                  }} />
                </div>
              )}
            </div>
          </button>
        );
      })()}

    </div>
  );

  // Launch column — launch button on its own row, then a slim status
  // strip below it for the extension / agent connection state. Stacked
  // vertically so the button stays the dominant CTA in the left column.
  const renderLaunchRow = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Row 1 — primary CTA + (optional) free-credits pill on the right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {!isRunning ? (() => {
          const quickApplyOut = !hasActiveSubscription && quickApplyCredits === 0;
          const baseDisabled = !extInstalled && !canStart;
          const disabled = baseDisabled || quickApplyOut;
          return (
            <button
              className="ajp-launch-btn"
              onClick={() => {
                if (quickApplyOut) {
                  window.dispatchEvent(new CustomEvent('openPricingModal'));
                  return;
                }
                if (preferences.platforms.linkedin) setShowLinkedInWarning(true);
                else handleLaunch();
              }}
              disabled={disabled}
              style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer', margin: 0, flex: 1, minWidth: 0 }}
              title={quickApplyOut ? 'Out of Quick Apply credits — upgrade to keep applying' : ''}
            >
              {quickApplyOut
                ? '🔒 Upgrade to keep applying'
                : extInstalled ? '⚡ Launch Auto Apply' : '🚀 Launch Auto Apply'}
            </button>
          );
        })() : (
          <button className="ajp-stop-btn" onClick={handleStop} style={{ margin: 0, flex: 1, minWidth: 0 }}>⏹️ Stop Agent</button>
        )}
        {!isRunning && !hasActiveSubscription && quickApplyCredits !== null && (
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '999px',
              fontSize: '0.7rem',
              fontWeight: 600,
              background: quickApplyCredits === 0 ? 'rgba(248,113,113,0.14)' : 'rgba(255,105,180,0.12)',
              border: `1px solid ${quickApplyCredits === 0 ? 'rgba(248,113,113,0.42)' : 'rgba(255,105,180,0.32)'}`,
              color: quickApplyCredits === 0 ? '#fca5a5' : '#e0a3ff',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
            title={`Free Quick Apply: ${quickApplyCredits} session${quickApplyCredits === 1 ? '' : 's'} left · capped at 5 jobs per session`}
          >
            {quickApplyCredits === 0
              ? 'Upgrade for unlimited'
              : `${quickApplyCredits} free · 5 jobs each`}
          </div>
        )}
      </div>

      {/* Row 2 — full-width status strip under the button. Dot + label,
          gentle background so it reads as connected secondary chrome. */}
      <div className="ajp-launch-status">
        {extInstalled ? (
          <>
            <div className="ajp-ext-status-dot" />
            <span>
              {isRunning ? `Applying jobs... (${stats.applied} applied)` : '⚡ Terview Extension ready'}
            </span>
          </>
        ) : (
          <>
            <div className={`ajp-ws-dot ${wsConnected ? 'connected' : 'disconnected'}`} />
            <span>{wsConnected ? 'Agent connected' : 'Agent disconnected'}</span>
          </>
        )}
        {sessionId && !isRunning && stats.applied > 0 && (
          <button
            className="ajp-btn-secondary"
            onClick={() => navigate('/dashboard')}
            style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: '0.72rem' }}
          >
            📊 Dashboard
          </button>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="ajp-form-grid">
      {/* ── Inline stats bar ── */}
      <div className="ajp-stats-bar">
        <div className="ajp-stats-bar-item ajp-stats-bar--total">
          <span className="ajp-stats-bar-num">{stats.applied + stats.skipped + stats.failed}</span>
          <span className="ajp-stats-bar-label">Total</span>
        </div>
        <div className="ajp-stats-bar-divider" />
        <div className="ajp-stats-bar-item ajp-stats-bar--applied">
          <span className="ajp-stats-bar-num">{stats.applied}</span>
          <span className="ajp-stats-bar-label">Applied</span>
        </div>
        <div className="ajp-stats-bar-divider" />
        <div className="ajp-stats-bar-item ajp-stats-bar--failed">
          <span className="ajp-stats-bar-num">{stats.failed}</span>
          <span className="ajp-stats-bar-label">Failed</span>
        </div>
        <div className="ajp-stats-bar-divider" />
        <div className="ajp-stats-bar-item ajp-stats-bar--skipped">
          <span className="ajp-stats-bar-num">{stats.skipped}</span>
          <span className="ajp-stats-bar-label">Skipped</span>
        </div>
      </div>

      {/* In-column tab nav removed — see the top-level pill switcher
          rendered right under the hero (above the form columns). */}

      {/* ─── LIVE TAB ─── */}
      {activeTab === 'live' && (
        <LiveTab
          logs={logs}
          isRunning={isRunning}
          consoleBodyRef={consoleBodyRef}
          consoleEndRef={consoleEndRef}
          appliedJobs={appliedJobs}
          extInstalled={extInstalled}
          user={user}
          preferences={preferences}
          setCanStart={setCanStart}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      {/* QueueTab now renders at the top level outside the two-column form,
          gated by activeTab === 'queue'. See main return below. */}
    </div>
  );

  return (
    <div className={embedded ? 'auto-apply-embedded-root' : 'neural-glass-homepage ajp-page'}>
      {/* Skip the global maroon neural-background + floating geometric shapes
          here so the page can wear the dashboard's calmer purple/cyan radial
          gradient (defined on .ajp-page in AutoJobApply.css). */}
      {!embedded && <Header setShowAuthModal={setShowAuthModal} />}
      {!embedded && <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />}

      {/* Block navigation while agent is running */}
      {showNavBlockModal && (
        <div className="ajp-nav-block-overlay" onClick={() => setShowNavBlockModal(false)}>
          <div className="ajp-nav-block-modal glass" onClick={e => e.stopPropagation()}>
            <div className="ajp-nav-block-icon">⚠️</div>
            <h3>Agent is still running</h3>
            <p>Leaving this page will not stop the automation. Do you want to stop the agent first?</p>
            <div className="ajp-nav-block-actions">
              <button
                className="ajp-nav-block-stop"
                onClick={async () => {
                  await handleStop();
                  setShowNavBlockModal(false);
                  if (pendingNavUrl) navigate(pendingNavUrl);
                }}
              >
                Stop Agent &amp; Leave
              </button>
              <button className="ajp-nav-block-stay" onClick={() => { setShowNavBlockModal(false); setPendingNavUrl(null); }}>
                Stay on Page
              </button>
            </div>
          </div>
        </div>
      )}

      <main className={`ajp-main ajp-main--wide ${embedded ? 'ajp-embedded' : ''}`}>

        {/* ─── Top-level mode switcher (pill toggle) ───
            Replaces the buried in-column tabs so users can flip between
            QuickApply (live browser-extension flow on LinkedIn / Indeed /
            Dice / Monster) and Auto Apply (24/7 server-side queue across
            Greenhouse / Lever / Workable / Ashby) right under the hero.
            One page, two modes, instant switch. */}
        {!embedded && (
          <div style={{
            display: 'flex', justifyContent: 'center', marginTop: 14, marginBottom: 14,
          }}>
            <div role="tablist" aria-label="Application mode" style={{
              display: 'inline-flex', padding: 5, borderRadius: 999,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))',
              border: '1px solid rgba(224,163,255,0.22)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.30), 0 0 0 1px rgba(255,255,255,0.04) inset',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
            }}>
              {[
                { key: 'live',  label: 'QuickApply' },
                { key: 'queue', label: 'Auto Apply' },
              ].map(({ key, label }) => {
                const active = activeTab === key;
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(key)}
                    style={{
                      padding: '10px 28px',
                      borderRadius: 999,
                      border: 'none',
                      background: active ? '#7c3aed' : 'transparent',
                      color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: 'pointer',
                      transition: 'background 0.18s ease, color 0.18s ease',
                      boxShadow: active ? '0 4px 18px rgba(124,58,237,0.45)' : 'none',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* QuickApply mode shows the live setup: preferences + resume + launch
            button + Live Application Console. None of those apply to the
            24/7 Auto Apply mode (which reads job titles/locations from the
            user profile, the resume from the saved profile, drains the queue
            autonomously, and shows its own per-plan quota counters in the
            QueueTab). So we render the two-col block ONLY in QuickApply mode. */}
        {activeTab === 'live' && (
          <div className="ajp-two-col">
            {/* ── LEFT: job preferences + platforms + resume + launch ── */}
            <div className="ajp-left-col glass">
              {renderStep1()}
              <div className="ajp-col-divider" />
              {renderStep2()}
              <div className="ajp-col-divider" />
              {renderLaunchRow()}
            </div>

            {/* ── RIGHT: stats bar + live application console ── */}
            <div className="ajp-right-col glass">
              {renderStep3()}
            </div>
          </div>
        )}

        {/* Auto Apply mode renders the queue full-width, no preferences form
            in front of it. Profile + resume come from /profile, not this page. */}
        {activeTab === 'queue' && (
          <QueueTab
            usage={usage}
            resetCountdown={resetCountdown}
            queueJobs={queueJobs}
            queueCounts={queueCounts}
            queueLoading={queueLoading}
            queueRefreshing={queueRefreshing}
            queueMode={queueMode}
            setQueueMode={setQueueMode}
            queueThreshold={queueThreshold}
            setQueueThreshold={setQueueThreshold}
            queueStatusFilter={queueStatusFilter}
            setQueueStatusFilter={setQueueStatusFilter}
            queueSettingsSavedAt={queueSettingsSavedAt}
            refreshQueue={refreshQueue}
            onDrainNow={drainNow}
            queueDraining={queueDraining}
            togglePause={togglePause}
            saveQueueSettings={saveQueueSettings}
            decideJob={decideJob}
            navigate={navigate}
            setActiveTab={setActiveTab}
            nylas={nylas}
            onConnectNylas={connectNylas}
            onDisconnectNylas={disconnectNylas}
            apiBaseUrl={API_BASE_URL}
          />
        )}
      </main>
      {showLinkedInWarning && (
        <div className="ajp-modal-overlay" onClick={() => setShowLinkedInWarning(false)}>
          <div className="ajp-modal glass" onClick={e => e.stopPropagation()}>
            <div className="ajp-modal-icon">⚠️</div>
            <h3 className="ajp-modal-title">LinkedIn Automation Warning</h3>
            <p className="ajp-modal-body">
              LinkedIn's Terms of Service prohibit automated job applications. While Terview automates <strong>your own account</strong> on your behalf, LinkedIn may:
            </p>
            <ul className="ajp-modal-list">
              <li>Temporarily restrict or permanently ban your account</li>
              <li>Flag your profile for suspicious activity</li>
            </ul>
            <p className="ajp-modal-body">
              By continuing, you acknowledge that <strong>you use LinkedIn automation at your own risk</strong>. Terview is not liable for any account restrictions.
            </p>
            <div className="ajp-modal-actions">
              <button className="ajp-modal-cancel" onClick={() => setShowLinkedInWarning(false)}>Cancel</button>
              <button className="ajp-modal-confirm" onClick={() => { setShowLinkedInWarning(false); handleLaunch(); }}>I Understand, Continue</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AutoJobApply;